import { groupLifeService } from '@/services';
import { GROUP_LIFE_RERATE_FAILURE_MESSAGE } from '@/features/quotes/hooks/group-life/use-members-rating-progress.hook';
import type {
  GroupLifeCalculateAnalysisPayload,
  GroupLifeCalculateAnalysisResult,
  GroupLifeQuoteDetails,
} from '@/services/group-life';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

type Variables = {
  policyId: string;
  payload: GroupLifeCalculateAnalysisPayload;
};

/** A re-tarifação em paralelo estoura o gateway se a rota esperar.
 *  O back devolve `processing` e o prêmio chega no GET seguinte.
 *  15 min cobre o lote de 15 no UAT e cotações maiores, em que
 *  as chamadas passam a enfileirar. */
const PREMIUM_POLL_INTERVAL_MS = 2_000;
const PREMIUM_POLL_TIMEOUT_MS = 15 * 60_000;

function premiumFingerprint(details?: GroupLifeQuoteDetails): string {
  const calc = details?.calculation;
  if (!calc) return '';
  const lines = (calc.breakdown ?? [])
    .map(line => `${line.description}:${line.total}`)
    .join(',');
  return `${calc.totalPremium}|${calc.netPremium}|${calc.iof}|${calc.monthlyPremium ?? ''}|${lines}`;
}

function readCachedDetails(
  queryClient: QueryClient,
  policyId: string
): GroupLifeQuoteDetails | undefined {
  const live = queryClient.getQueryData<GroupLifeQuoteDetails>([
    'group-life',
    'quote-details',
    policyId,
  ]);
  if (live) return live;
  const versioned = queryClient
    .getQueriesData<GroupLifeQuoteDetails>({
      queryKey: ['group-life', 'quote-at-version', policyId],
    })
    .map(([, data]) => data)
    .find((data): data is GroupLifeQuoteDetails => data != null);
  return versioned;
}

function isGatewayDrop(status?: number, error?: string): boolean {
  if (status === 502 || status === 503 || status === 504) return true;
  if (status != null) return false;
  const message = (error ?? '').toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('network request failed') ||
    message.includes('load failed') ||
    message.includes('timeout')
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function waitForPremiumChange(
  policyId: string,
  baseline: string
): Promise<GroupLifeCalculateAnalysisResult> {
  // Sem snapshot anterior, a primeira leitura É o valor velho — só conta
  // como pronto quando o prêmio mudar em relação a ela.
  let seen = baseline;
  const deadline = Date.now() + PREMIUM_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const progress = await groupLifeService.getMembersRatingProgress(policyId);
    if (progress.success && progress.data?.status === 'failed') {
      throw new Error(GROUP_LIFE_RERATE_FAILURE_MESSAGE);
    }

    const response = await groupLifeService.getQuoteDetails(policyId);
    const next = premiumFingerprint(response.data);
    if (response.success && response.data && next) {
      if (seen && next !== seen) {
        return {
          outcome: 'recalculated',
          details: response.data,
          totalPremium: response.data.calculation?.totalPremium,
        };
      }
      if (!seen) seen = next;
    }
    await sleep(PREMIUM_POLL_INTERVAL_MS);
  }
  throw new Error(
    'O recálculo continua no servidor. Aguarde alguns instantes e atualize a cotação antes de tentar de novo.'
  );
}

export function useCalculateGroupLifeAnalysis() {
  const queryClient = useQueryClient();

  return useMutation<GroupLifeCalculateAnalysisResult, Error, Variables>({
    mutationKey: ['group-life', 'calculate-analysis'],
    retry: false,
    mutationFn: async ({ policyId, payload }) => {
      const baseline = premiumFingerprint(
        readCachedDetails(queryClient, policyId)
      );
      const response = await groupLifeService.calculateAnalysis(
        policyId,
        payload
      );

      if (response.success && response.data?.outcome === 'unchanged') {
        return response.data;
      }

      // `processing`: a rota já gravou a comercialização e soltou a conexão.
      // 502/503/504: o gateway cortou a resposta, mas o cálculo segue e o
      // prêmio aparece no GET logo depois.
      if (
        (response.success && response.data?.outcome === 'processing') ||
        isGatewayDrop(response.status, response.error)
      ) {
        return waitForPremiumChange(policyId, baseline);
      }

      if (!response.success || !response.data) {
        throw new Error(
          response.error || 'Erro ao recalcular cotação em análise'
        );
      }
      return response.data;
    },
    onSuccess: (result, variables) => {
      const { policyId } = variables;
      const quoteDetailsKey = ['group-life', 'quote-details', policyId];

      // Nada mudou → nenhum cache ficou obsoleto.
      if (result.outcome === 'unchanged') return;

      if (result.details) {
        queryClient.setQueryData(quoteDetailsKey, result.details);
      } else {
        queryClient.invalidateQueries({ queryKey: quoteDetailsKey });
      }

      // Toda mudança de comercialização re-tarifa vida a vida, então os
      // caches por vida (tarifação, vidas) ficam obsoletos.
      queryClient.removeQueries({
        queryKey: ['pme-tarifacao', 'quote', policyId],
      });
      queryClient.setQueryData(
        ['pme-tarifacao-revision', 'quote', policyId],
        Date.now()
      );
      queryClient.invalidateQueries({
        queryKey: ['group-life', 'underwriting-messages', policyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['group-life', 'proposal-lives', policyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['group-life', 'members-page', policyId],
      });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quotes-status-count'] });
    },
  });
}
